import 'twin.macro'

import { Ring } from '@uiball/loaders'
import ArrowRightIcon from 'assets/icons/arrow-right.svg?component'
import { HTMLMotionProps, motion } from 'framer-motion'
import { Fragment, ReactNode } from 'react'
import tw, { TwStyle } from 'twin.macro'

type ButtonVariants = 'primary' | 'secondary' | 'outline' | 'ghost' | 'text' | 'link' | 'selector'
type ButtonSize = 'small' | 'medium' | 'big'

type ButtonProps = {
  variant?: ButtonVariants
  size?: ButtonSize
  children: ReactNode
  loading?: boolean
  twStyle?: TwStyle
  icon?: ReactNode
} & HTMLMotionProps<'button'>

type SharedButtonProps = Omit<ButtonProps, 'twStyle, variant'>

const styles = {
  button: ({ variant, size, twStyle }: { twStyle?: TwStyle; size?: ButtonSize; variant: ButtonVariants }) => [
    tw`inline-flex justify-center items-center disabled:opacity-60 disabled:cursor-not-allowed min-w-button cursor-pointer`,
    buttonVariantStyles({ variant, size }),
    twStyle
  ]
}

const buttonVariantStyles = ({ variant, size }: { variant: ButtonVariants; size?: ButtonSize }) =>
  ({
    primary: {
      ...tw`md:max-w-button  justify-start py-5 pl-6 pr-4 rounded-[1.875em] bg-primary-middle w-full`,
      ...(size && tw`w-auto min-w-min`)
    },
    secondary: {
      ...tw`py-[1.125em] bg-primary-middle rounded-[1.875em] text-white shadow-button w-full`,
      ...(size && tw`py-3 px-5 w-auto min-w-min`)
    },
    outline: tw`py-[1.125em] bg-white border border-[var(--color-gray)] rounded-[1.875em] text-primary-middle w-full`,
    ghost: tw``,
    text: tw`py-[18px] px-6 text-primary-middle text-base leading-5`,
    link: tw`text-primary-middle text-sm leading-s18 min-w-fit font-bold py-2`,
    selector: tw`bg-toggle-button rounded-[0.875em] p-[0.875em] border border-primary-middle text-primary-middle`
  }[variant])

const getButtonByVariant = ({ variant = 'secondary', children, ...props }: ButtonProps) =>
  ({
    primary: (
      <PrimaryButton variant={variant} {...props}>
        {children}
      </PrimaryButton>
    ),
    secondary: (
      <SecondaryButton variant={variant} {...props}>
        {children}
      </SecondaryButton>
    ),
    ghost: (
      <SecondaryButton variant={variant} {...props}>
        {children}
      </SecondaryButton>
    ),
    text: (
      <SecondaryButton variant={variant} {...props}>
        {children}
      </SecondaryButton>
    ),
    link: (
      <SecondaryButton variant={variant} {...props}>
        {children}
      </SecondaryButton>
    ),
    selector: (
      <SecondaryButton variant={variant} {...props}>
        {children}
      </SecondaryButton>
    ),
    outline: (
      <SecondaryButton variant={variant} {...props}>
        {children}
      </SecondaryButton>
    )
  }[variant])

const Button = ({
  variant = 'secondary',
  size,
  children,
  loading,
  icon,
  twStyle,
  disabled,
  ...buttonProps
}: ButtonProps) => {
  return (
    <motion.button
      css={styles.button({ variant, size, twStyle })}
      whileTap={{ scale: !disabled || !loading ? 0.97 : 1 }}
      disabled={disabled || loading}
      {...buttonProps}
    >
      {getButtonByVariant({ variant, children, loading, size, icon })}
    </motion.button>
  )
}

const PrimaryButton = ({ children, loading, icon }: SharedButtonProps) => {
  return (
    <div className='text-ellipsis' tw='flex items-center w-full text-white space-x-4 justify-between overflow-hidden'>
      <div className='text-start'>{children}</div>
      <div tw='h-10 w-10 bg-white rounded-[14px] flex items-center justify-center flex-[0 0 auto]'>
        {loading ? (
          <Ring size={24} color='var(--primary-color)' />
        ) : (
          <Fragment>{icon ? icon : <ArrowRightIcon tw='flex-shrink-0 w-4 text-black' />}</Fragment>
        )}
      </div>
    </div>
  )
}

const SecondaryButton = ({ variant, children, loading, size }: SharedButtonProps) => {
  return (
    <span className='text-ellipsis overflow-hidden'>
      {loading ? (
        <Ring
          size={size === 'small' ? 16 : 24}
          color={variant === 'outline' ? 'var(--primary-color)' : 'var(--white)'}
        />
      ) : (
        children
      )}
    </span>
  )
}

export default Button
